/**
 * Seller bulk uploads — many products, or many stock levels, from one sheet.
 *
 * The seller's browser parses the CSV and sends typed rows; the server
 * validates every row again and answers per row. Each row carries the line it
 * came from so an error can be pointed at the cell that caused it.
 *
 * Row fields are typed only loosely here on purpose (any integer price, any
 * HSN text): the business rules — price within MRP, a real GST slab, a known
 * category — are checked per row by the service, so one bad cell costs that
 * row an error message instead of rejecting the whole upload with a 422.
 */
import { z } from 'zod';

/** Rows per request. The seller app sends a file in chunks of whole products. */
export const BULK_PRODUCT_MAX_ROWS = 100;
export const BULK_STOCK_MAX_ROWS = 1000;
export const BULK_MAX_IMAGES = 4;
/** Image links per request — each is a download, so a chunk stays quick to answer. */
export const BULK_MAX_IMAGES_PER_REQUEST = 40;

const Line = z.number().int().positive();
const Text = (max: number) => z.string().trim().max(max);

export const BulkProductRowSchema = z
  .object({
    line: Line,
    /** Rows sharing a handle are variants of one product; blank = a product of its own. */
    handle: Text(100).optional(),
    title: Text(300),
    /** An existing category's name or slug. Bulk upload never creates categories. */
    category: Text(200),
    brand: Text(100).optional(),
    description: Text(10_000).optional(),
    bulletPoints: z.array(Text(500)).max(10).optional(),
    hsnSac: Text(8),
    gstRate: z.number().optional(),
    sku: Text(100),
    mrpPaise: z.number().int(),
    pricePaise: z.number().int(),
    weightGrams: z.number().int().optional(),
    barcode: Text(30).optional(),
    stock: z.number().int().optional(),
    /** `{ Size: 'M', Colour: 'Red' }`; empty for a product without variants. */
    options: z.record(Text(60)).optional(),
    /** Public https image links; read from the first row of a product only. */
    imageUrls: z.array(z.string().trim().max(2000)).max(BULK_MAX_IMAGES).optional(),
  })
  .strict();
export type BulkProductRow = z.infer<typeof BulkProductRowSchema>;

export const BulkProductRequestSchema = z
  .object({
    dryRun: z.boolean(),
    /** Send each created product for review straight away. */
    submitForApproval: z.boolean().default(false),
    rows: z.array(BulkProductRowSchema).min(1).max(BULK_PRODUCT_MAX_ROWS),
  })
  .strict()
  .refine(
    (b) =>
      b.rows.reduce((n, r) => n + (r.imageUrls?.length ?? 0), 0) <= BULK_MAX_IMAGES_PER_REQUEST,
    {
      message: `At most ${BULK_MAX_IMAGES_PER_REQUEST} image links per upload request — send fewer products at a time`,
      path: ['rows'],
    },
  );
export type BulkProductRequest = z.infer<typeof BulkProductRequestSchema>;

export const BulkRowStatusSchema = z.enum(['OK', 'ERROR', 'SKIPPED']);
export type BulkRowStatus = z.infer<typeof BulkRowStatusSchema>;

export interface BulkProductRowResult {
  line: number;
  sku: string;
  status: BulkRowStatus;
  /** Set once the product exists (live run only). */
  productId?: string;
  productStatus?: string;
  errors: string[];
  warnings: string[];
}

export interface BulkProductResponse {
  dryRun: boolean;
  products: { ok: number; failed: number };
  rows: BulkProductRowResult[];
}

// ── Stock ───────────────────────────────────────────────────────────────────

export const BulkStockRowSchema = z
  .object({
    line: Line,
    sku: Text(100),
    /** Warehouse name or id; blank = the seller's default warehouse. */
    warehouse: Text(120).optional(),
    /** The quantity physically on hand — the new level, not a change. */
    quantity: z.number().int(),
  })
  .strict();
export type BulkStockRow = z.infer<typeof BulkStockRowSchema>;

export const BulkStockRequestSchema = z
  .object({
    dryRun: z.boolean(),
    reason: Text(200).optional(),
    rows: z.array(BulkStockRowSchema).min(1).max(BULK_STOCK_MAX_ROWS),
  })
  .strict();
export type BulkStockRequest = z.infer<typeof BulkStockRequestSchema>;

export interface BulkStockRowResult {
  line: number;
  sku: string;
  warehouse: string | null;
  status: BulkRowStatus;
  onHandBefore?: number;
  onHandAfter?: number;
  reserved?: number;
  errors: string[];
}

export interface BulkStockResponse {
  dryRun: boolean;
  updated: number;
  unchanged: number;
  failed: number;
  rows: BulkStockRowResult[];
}
